import {
  ArrowLeft,
  Ellipsis,
  Image as ImageIcon,
  Mic,
  Send,
  ShieldCheck,
  Crown,
  LockKeyhole,
  ArrowDown,
} from "lucide-react";
import { FormEvent, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ApiError, api, json } from "../api";
import { useLocale, useSession } from "../App";
import type { Conversation, Message } from "../types";
import { BottomNav, OnlineDot, PhoneShell } from "../components/Shell";
import { VipPromptModal } from "../components/VipPromptModal";

export function ChatPage() {
  const { conversationId = "" } = useParams();
  const lang = useLocale();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();
  const [text, setText] = useState("");
  const [limit, setLimit] = useState(false);
  const [vipPrompt, setVipPrompt] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceNotice, setVoiceNotice] = useState("");
  const [newMessages, setNewMessages] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const voiceChunks = useRef<Blob[]>([]);
  const endRef = useRef<HTMLDivElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);
  const previousCountRef = useRef(0);
  const conversations = useQuery({
    queryKey: ["conversations", lang],
    queryFn: () =>
      api<{ conversations: Conversation[] }>(`/conversations?lang=${lang}`),
  });
  const conversation = conversations.data?.conversations.find(
    (item) => item.id === conversationId,
  );
  const messages = useQuery({
    queryKey: ["messages", conversationId, lang],
    queryFn: () =>
      api<{ messages: Message[] }>(
        `/conversations/${conversationId}/messages?lang=${lang}`,
      ),
    refetchInterval: 5000,
  });
  useEffect(() => {
    if (messages.data)
      void queryClient.invalidateQueries({ queryKey: ["conversations", lang] });
  }, [lang, messages.data, queryClient]);
  useEffect(() => {
    const count = messages.data?.messages.length ?? 0;
    if (previousCountRef.current === 0 || atBottomRef.current) {
      endRef.current?.scrollIntoView({ behavior: previousCountRef.current ? "smooth" : "auto" });
      setNewMessages(false);
    } else if (count > previousCountRef.current) {
      setNewMessages(true);
    }
    previousCountRef.current = count;
  }, [messages.data?.messages.length]);
  const send = useMutation({
    mutationFn: () => api("/messages", json("POST", { conversationId, text })),
    onSuccess: async () => {
      setText("");
      setLimit(false);
      await queryClient.invalidateQueries({
        queryKey: ["messages", conversationId, lang],
      });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === "VIP_REQUIRED")
        setLimit(true);
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (text.trim()) send.mutate();
  };
  const uploadVoice = async (blob: Blob) => {
    setVoiceNotice(lang === "vn" ? "Đang gửi tin nhắn thoại…" : "Sending voice message…");
    const body = new FormData();
    body.append("file", new File([blob], "voice.webm", { type: blob.type || "audio/webm" }));
    const queued = await api<{ upload: { id: string } }>("/uploads/audio", {
      method: "POST",
      body,
    });
    for (let attempt = 0; attempt < 60; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 500));
      const result = await api<{ upload: { status: string; failure_reason?: string } }>(
        `/uploads/${queued.upload.id}`,
      );
      if (result.upload.status === "ready") {
        await api(
          "/messages/audio",
          json("POST", { conversationId, uploadId: queued.upload.id }),
        );
        setVoiceNotice("");
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["messages", conversationId, lang] }),
          queryClient.invalidateQueries({ queryKey: ["conversations", lang] }),
        ]);
        return;
      }
      if (result.upload.status === "failed")
        throw new Error(result.upload.failure_reason ?? "Voice upload failed");
    }
    throw new Error("Voice processing timed out");
  };
  const toggleVoice = async () => {
    if (!session.data?.user?.isVip) {
      setVipPrompt(true);
      return;
    }
    if (recorderRef.current?.state === "recording") {
      recorderRef.current.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const recorder = new MediaRecorder(stream, { mimeType });
      voiceChunks.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size) voiceChunks.current.push(event.data);
      };
      recorder.onstop = () => {
        setRecording(false);
        stream.getTracks().forEach((track) => track.stop());
        const blob = new Blob(voiceChunks.current, { type: recorder.mimeType });
        void uploadVoice(blob).catch((error) => setVoiceNotice((error as Error).message));
      };
      recorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      setVoiceNotice(lang === "vn" ? "Đang ghi âm · chạm lại để gửi" : "Recording · tap again to send");
    } catch (error) {
      setVoiceNotice((error as Error).message);
    }
  };
  return (
    <PhoneShell className="chat-page">
      <header className="chat-header">
        <button onClick={() => navigate(`/${lang}/messages`)}>
          <ArrowLeft />
        </button>
        <div>
          <b>
            {conversation?.name ?? "…"} <OnlineDot />
          </b>
          <small>{conversation?.online ? t("online") : ""}</small>
        </div>
        <Ellipsis />
      </header>
      <div className="safety-note">
        <ShieldCheck />
        {t("safetyNote")}
      </div>
      <div
        className="message-thread"
        ref={threadRef}
        onScroll={(event) => {
          const target = event.currentTarget;
          atBottomRef.current = target.scrollHeight - target.scrollTop - target.clientHeight < 80;
          if (atBottomRef.current) setNewMessages(false);
        }}
      >
        {messages.data?.messages.map((message) => (
          <MessageBubble key={message.id} message={message} onVip={() => setVipPrompt(true)} />
        ))}
        <div ref={endRef} />
      </div>
      {newMessages && (
        <button
          className="new-message-pill"
          onClick={() => {
            atBottomRef.current = true;
            setNewMessages(false);
            endRef.current?.scrollIntoView({ behavior: "smooth" });
          }}
        >
          <ArrowDown /> {lang === "vn" ? "Tin nhắn mới" : "New message"}
        </button>
      )}
      {limit && (
        <div className="vip-prompt">
          <b>{t("vipRequired")}</b>
          <button onClick={() => navigate(`/${lang}/account`)}>
            {t("vipUnlock")}
          </button>
        </div>
      )}
      {voiceNotice?<div className="voice-notice" role="status">{voiceNotice}</div>:null}
      <form className="chat-composer" onSubmit={submit}>
        <button
          type="button"
          className={recording?"recording":""}
          title={t("voiceLocked")}
          onClick={()=>void toggleVoice()}
        >
          <Mic />
        </button>
        <input
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={t("messagePlaceholder")}
          maxLength={1000}
        />
        <ImageIcon className="disabled-icon" />
        <button
          className="send-button"
          disabled={!text.trim() || send.isPending}
        >
          <Send />
        </button>
      </form>
      <BottomNav />
      <VipPromptModal open={vipPrompt} onClose={()=>setVipPrompt(false)}/>
    </PhoneShell>
  );
}

function MessageBubble({ message, onVip }: { message: Message; onVip: () => void }) {
  const time = new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(message.created_at));
  return (
    <div className={`message-row ${message.sender_kind}`}>
      <div className="bubble">
        {message.locked ? (
          message.message_type === "audio" ? (
            <button className="locked-audio-message" onClick={onVip}>
              <LockKeyhole /><span>VIP voice message</span><Crown />
            </button>
          ) : (
            <button className="locked-media-message" onClick={onVip}>
              {message.preview_url && <img src={message.preview_url} alt="Locked greeting" />}
              <span><LockKeyhole /> VIP</span>
            </button>
          )
        ) : message.message_type === "image" ? (
          <img
            src={message.content ?? ""}
            alt="Greeting"
            loading="lazy"
            decoding="async"
          />
        ) : message.message_type === "audio" ? (
          <audio controls preload="metadata" src={message.content ?? ""} />
        ) : message.message_type === "video" ? (
          <video controls preload="metadata" playsInline src={message.content ?? ""} />
        ) : (
          <span>{message.content}</span>
        )}
        <time>
          {time}
          {message.sender_kind === "male" && " ✓✓"}
        </time>
      </div>
    </div>
  );
}
